import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
  Chip,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Tabs,
  Tab,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemText as MUIListItemText,
  ListItemAvatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  GetApp as DownloadIcon,
  Print as PrintIcon,
  AttachMoney as AdvanceIcon,
  AccountBalance as PaymentIcon,
  Receipt as ExpenseIcon,
  Assignment as ReportIcon,
  CheckCircle as ApprovedIcon,
  Schedule as PendingIcon,
  Cancel as RejectedIcon,
  MonetizationOn as MoneyIcon,
  Person as PersonIcon,
  DateRange as DateIcon,
  Business as TravelIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Send as SubmitIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  AccountBalanceWallet as WalletIcon,
  CreditCard as CardIcon,
  TrendingUp as OutstandingIcon,
  TrendingDown as SettledIcon,
  Flight as FlightIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, differenceInDays, addDays } from "date-fns";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/slices/authSlice";
import { useAuth } from "../../components/features/auth/AuthGuard";
import { ROUTES, ROLES, PERMISSIONS } from "../../constants";
import useNotification from "../../hooks/common/useNotification";
import useConfirmDialog from "../../hooks/common/useConfirmDialog";
import { LoadingSpinner } from "../../components/common/Loading";
import { travelAPI } from "../../services/api/travel.api";

const TravelAdvancesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get("requestId");
  const user = useSelector(selectUser);
  const { hasPermission, hasAnyRole } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { isOpen, config, openDialog, closeDialog, handleConfirm } =
    useConfirmDialog();

  // State
  const [loading, setLoading] = useState(false);
  const [travelAdvances, setTravelAdvances] = useState([]);
  const [approvedTravelRequests, setApprovedTravelRequests] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedAdvance, setSelectedAdvance] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [advanceDetails, setAdvanceDetails] = useState(null);
  const [viewDetailsLoading, setViewDetailsLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedTravelRequest, setSelectedTravelRequest] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAdvance, setEditingAdvance] = useState(null);
  const [editFormData, setEditFormData] = useState({
    departure_date: null,
    return_date: null,
    total_cost: "",
  });

  // Form state for creating advance
  const [formData, setFormData] = useState({
    travel_request_id: "",
    advance_number: "",
    total_cost: "",
    payment_method: "",
    currency: "TZS",
    justification: "",
    notes: "",
  });

  // Status definitions (backend: draft, approved by supervisor, approved by finance, rejected)
  const advanceStatuses = {
    draft: { label: "Pending", color: "warning" },
    pending: { label: "Pending", color: "warning" },
    "approved by supervisor": { label: "Dept Head Approved", color: "info" },
    department_head_approved: { label: "Dept Head Approved", color: "info" },
    "approved by finance": { label: "Finance Approved", color: "primary" },
    finance_manager_approved: { label: "Finance Approved", color: "primary" },
    admin_approved: { label: "Admin Approved", color: "success" },
    rejected: { label: "Rejected", color: "error" },
  };

  // Who is next to approve (for follow-up when not yet approved)
  const getNextApprover = (approvalStatus) => {
    const s = approvalStatus || "";
    if (s === "rejected" || s === "approved by finance" || s === "finance_manager_approved" || s === "admin_approved") return null;
    if (s === "approved by supervisor" || s === "department_head_approved") return "Finance";
    if (s === "draft" || s === "pending" || s === "") return "Department Head";
    return "Department Head";
  };

  const paymentStatuses = {
    pending: { label: "Pending", color: "warning" },
    pending_payment: { label: "Pending Payment", color: "warning" },
    paid: { label: "Paid", color: "success" },
    partially_disbursed: { label: "Partially Disbursed", color: "info" },
    disbursed: { label: "Disbursed", color: "success" },
    settled: { label: "Settled", color: "success" },
  };

  // Payment methods
  const paymentMethods = [
    { value: "bank_transfer", label: "Bank Transfer", icon: <PaymentIcon /> },
    { value: "cash", label: "Cash", icon: <MoneyIcon /> },
    { value: "mobile_money", label: "Mobile Money", icon: <CardIcon /> },
    { value: "check", label: "Check", icon: <PaymentIcon /> },
  ];

  // Summary cards data
  const summaryCards = [
    {
      title: "Total Advances",
      value: travelAdvances.length.toString(),
      icon: <AdvanceIcon />,
      color: "primary",
    },
    {
      title: "Outstanding Amount",
      value: `TZS ${travelAdvances
        .reduce((sum, adv) => sum + (adv.outstanding_amount || 0), 0)
        .toLocaleString()}`,
      icon: <OutstandingIcon />,
      color: "warning",
    },
    {
      title: "Settled This Month",
      value: `TZS ${travelAdvances
        .filter((adv) => adv.payment_status === "settled" || adv.payment_status === "paid")
        .reduce((sum, adv) => sum + (adv.disbursed_amount || adv.total_cost || 0), 0)
        .toLocaleString()}`,
      icon: <SettledIcon />,
      color: "success",
    },
    {
      title: "Pending Settlement",
      value: travelAdvances
        .filter((adv) => adv.payment_status === "pending" || adv.payment_status === "pending_payment")
        .length.toString(),
      icon: <WarningIcon />,
      color: "error",
    },
  ];

  // DataGrid columns
  const columns = [
    {
      field: "advance_number",
      headerName: "Advance #",
      width: 130,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AdvanceIcon fontSize="small" color="primary" />
          <Typography variant="body2" fontWeight="medium">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "employee_info",
      headerName: "Employee",
      width: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.row.employee_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.department} • {params.row.employee_id}
          </Typography>
        </Box>
      ),
    },
    {
      field: "travel_info",
      headerName: "Travel Details",
      width: 250,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.row.travel_request_title || params.row.travel_destination}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {format(new Date(params.row.departure_date), "dd/MM/yyyy")} -{" "}
            {format(new Date(params.row.return_date), "dd/MM/yyyy")}
          </Typography>
        </Box>
      ),
    },
    {
      field: "advance_amount",
      headerName: "Advance Amount",
      width: 130,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          TZS {params.value?.toLocaleString()}
        </Typography>
      ),
    },
    {
      field: "payment_status",
      headerName: "Payment Status",
      width: 150,
      renderCell: (params) => {
        const status = paymentStatuses[params.value] || paymentStatuses.pending;
        return (
          <Chip
            label={status.label}
            color={status.color}
            size="small"
            variant="outlined"
          />
        );
      },
    },
    {
      field: "status",
      headerName: "Approval Status",
      width: 200,
      renderCell: (params) => {
        const approvalStatus = params.row.approval_status ?? params.value;
        const status = advanceStatuses[approvalStatus] || advanceStatuses.pending;
        const nextApprover = getNextApprover(approvalStatus);
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, py: 0.5 }}>
            <Chip
              label={status.label}
              color={status.color}
              size="small"
              variant="outlined"
            />
            {nextApprover && (
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                Awaiting: {nextApprover}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: "created_date",
      headerName: "Created",
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {format(new Date(params.value), "dd/MM/yyyy")}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          onClick={(e) => handleActionClick(e, params.row)}
          size="small"
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  // Fetch data
  useEffect(() => {
    fetchTravelAdvances();
    fetchApprovedTravelRequests();
  }, []);

  const fetchTravelAdvances = async () => {
    try {
      setLoading(true);
      const response = await travelAPI.getAllAdvances();
      const raw = response.data.data?.advances || response.data?.advances || [];
      // Normalize API shape for list (user nested, total_cost, approval_status)
      setTravelAdvances(
        raw.map((adv) => ({
          ...adv,
          employee_name: [adv.user?.first_name, adv.user?.sur_name].filter(Boolean).join(" ") || "—",
          employee_id: adv.user_id,
          advance_amount: adv.total_cost,
          advance_number: adv.advance_number || `TA-${adv.id}`,
          status: adv.approval_status,
          travel_destination: adv.destination || "—",
          created_date: adv.created_at,
        }))
      );
    } catch (error) {
      console.error("Error fetching travel advances:", error);
      showError("Failed to fetch travel advances");
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovedTravelRequests = async () => {
    try {
      const response = await travelAPI.getApprovedTravelRequests();
      const data = response.data?.data || response.data;
      setApprovedTravelRequests(
        data?.travelRequests || []
      );
    } catch (error) {
      console.error("Error fetching approved travel requests:", error);
      showError("Failed to fetch approved travel requests");
    }
  };

  // Handle form changes
  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle travel request selection
  const handleTravelRequestSelect = (travelRequest) => {
    setSelectedTravelRequest(travelRequest);
    setFormData((prev) => ({
      ...prev,
      travel_request_id: travelRequest.id,
      total_cost: travelRequest.advance_amount || travelRequest.estimated_cost,
      advance_number: `TA-${new Date().getFullYear()}-${String(travelRequest.id).padStart(3, "0")}`,
    }));
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Ensure total_cost is a number
      const submitData = {
        ...formData,
        total_cost: parseFloat(formData.total_cost) || 0,
      };

      console.log("Submitting form data:", submitData);
      await travelAPI.createAdvance(submitData);
      showSuccess("Travel advance created successfully");
      setCreateDialogOpen(false);
      resetForm();
      fetchTravelAdvances();
    } catch (error) {
      console.error("Error creating travel advance:", error);
      console.error("Error response:", error.response?.data);
      showError("Failed to create travel advance");
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      travel_request_id: "",
      advance_number: "",
      total_cost: "",
      payment_method: "",
      currency: "TZS",
      justification: "",
      notes: "",
    });
    setSelectedTravelRequest(null);
  };

  // Handle action menu
  const handleActionClick = (event, advance) => {
    setSelectedAdvance(advance);
    setActionMenuAnchor(event.currentTarget);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
  };

  // Handle resubmit
  const handleResubmit = async (advance) => {
    try {
      setLoading(true);
      await travelAPI.resubmitTravelAdvance(advance.id);
      showSuccess("Travel advance resubmitted successfully");
      fetchTravelAdvances();
    } catch (error) {
      console.error("Error resubmitting travel advance:", error);
      showError("Failed to resubmit travel advance");
    } finally {
      setLoading(false);
    }
  };

  // Handle view details: open dialog and fetch full advance (with travel request, etc.)
  const handleViewDetails = (advance) => {
    setSelectedAdvance(advance);
    setAdvanceDetails(null);
    setViewDialogOpen(true);
  };

  // Fetch full advance when view details dialog opens
  useEffect(() => {
    if (!viewDialogOpen || !selectedAdvance?.id) {
      setAdvanceDetails(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setViewDetailsLoading(true);
      setAdvanceDetails(null);
      try {
        const response = await travelAPI.getAdvanceById(selectedAdvance.id);
        const data = response?.data?.data ?? response?.data;
        if (!cancelled && data) setAdvanceDetails(data);
      } catch (err) {
        if (!cancelled) showError("Failed to load advance details");
      } finally {
        if (!cancelled) setViewDetailsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [viewDialogOpen, selectedAdvance?.id]);

  // Can current user edit this advance (draft or rejected, and owner or has permission)
  const canEditAdvance = (advance) => {
    if (!advance) return false;
    const isDraftOrRejected =
      advance.status === "draft" || advance.status === "rejected" || advance.approval_status === "draft" || advance.approval_status === "rejected";
    const isOwner = advance.user_id === user?.id;
    const hasUpdate =
      hasPermission?.(PERMISSIONS.MANAGE_TRAVEL_ADVANCES) || hasPermission?.("travel_advances:update");
    return isDraftOrRejected && (isOwner || hasUpdate);
  };

  // Handle edit (open edit dialog)
  const handleEdit = (advance) => {
    setEditingAdvance(advance);
    setEditFormData({
      departure_date: advance.departure_date ? new Date(advance.departure_date) : null,
      return_date: advance.return_date ? new Date(advance.return_date) : null,
      total_cost: advance.total_cost != null ? String(advance.total_cost) : advance.advance_amount != null ? String(advance.advance_amount) : "",
    });
    setEditDialogOpen(true);
    setViewDialogOpen(false);
    setActionMenuAnchor(null);
  };

  // Handle edit form save
  const handleEditSave = async () => {
    if (!editingAdvance?.id) return;
    try {
      setLoading(true);
      const totalCostRaw = String(editFormData.total_cost || "").replace(/,/g, "");
      const payload = {
        departure_date: editFormData.departure_date ? format(editFormData.departure_date, "yyyy-MM-dd") : undefined,
        return_date: editFormData.return_date ? format(editFormData.return_date, "yyyy-MM-dd") : undefined,
        total_cost: parseFloat(totalCostRaw) || 0,
      };
      await travelAPI.updateAdvance(editingAdvance.id, payload);
      showSuccess("Travel advance updated. You can resubmit when ready.");
      setEditDialogOpen(false);
      setEditingAdvance(null);
      fetchTravelAdvances();
    } catch (error) {
      console.error("Error updating travel advance:", error);
      showError(error.response?.data?.message || "Failed to update travel advance");
    } finally {
      setLoading(false);
    }
  };

  // Filtered data
  const filteredAdvances = travelAdvances.filter((advance) => {
    const matchesSearch =
      advance.advance_number
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      advance.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      advance.travel_destination
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || advance.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Travel Advances
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          disabled={!hasPermission(PERMISSIONS.CREATE_TRAVEL_ADVANCES)}
        >
          Create Advance
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {summaryCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color={`${card.color}.main`}
                    >
                      {card.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {card.title}
                    </Typography>
                  </Box>
                  <Box sx={{ color: `${card.color}.main` }}>{card.icon}</Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
        >
          <Tab label="All Advances" />
          <Tab label="Create Advance" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {selectedTab === 0 && (
        <Box>
          {/* Filters */}
          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <TextField
              placeholder="Search advances..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 300 }}
            />
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Statuses</MenuItem>
                {Object.entries(advanceStatuses).map(([key, status]) => (
                  <MenuItem key={key} value={key}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Data Grid */}
          <Card>
            <DataGrid
              rows={filteredAdvances}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              autoHeight
              sx={{ border: "none" }}
            />
          </Card>
        </Box>
      )}

      {selectedTab === 1 && (
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Select Approved Travel Request
              </Typography>

              {approvedTravelRequests.length === 0 ? (
                <Alert severity="info">
                  No approved travel requests found. Travel requests must be
                  fully approved before creating advances.
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {approvedTravelRequests.map((request) => (
                    <Grid item xs={12} md={6} key={request.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          cursor: "pointer",
                          borderColor:
                            selectedTravelRequest?.id === request.id
                              ? "primary.main"
                              : "divider",
                          "&:hover": { borderColor: "primary.main" },
                        }}
                        onClick={() => handleTravelRequestSelect(request)}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <FlightIcon color="primary" />
                            <Typography variant="h6">
                              {request.title}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            {request.destination} • {request.employee_name}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {format(
                              new Date(request.departure_date),
                              "dd/MM/yyyy"
                            )}{" "}
                            -{" "}
                            {format(
                              new Date(request.return_date),
                              "dd/MM/yyyy"
                            )}
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            Advance Amount: TZS{" "}
                            {request.advance_amount?.toLocaleString() ||
                              request.estimated_cost?.toLocaleString()}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Create Advance Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Travel Advance</DialogTitle>
        <DialogContent>
          {selectedTravelRequest && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Creating advance for:{" "}
              <strong>{selectedTravelRequest.title}</strong> -{" "}
              {selectedTravelRequest.destination}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Advance Number"
                value={formData.advance_number}
                onChange={(e) =>
                  handleFormChange("advance_number", e.target.value)
                }
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Total Cost"
                type="number"
                value={formData.total_cost}
                onChange={(e) => handleFormChange("total_cost", e.target.value)}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">TZS</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={formData.payment_method}
                  onChange={(e) =>
                    handleFormChange("payment_method", e.target.value)
                  }
                  label="Payment Method"
                >
                  {paymentMethods.map((method) => (
                    <MenuItem key={method.value} value={method.value}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {method.icon}
                        {method.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Currency"
                value={formData.currency}
                onChange={(e) => handleFormChange("currency", e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Justification"
                multiline
                rows={3}
                value={formData.justification}
                onChange={(e) =>
                  handleFormChange("justification", e.target.value)
                }
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => handleFormChange("notes", e.target.value)}
                margin="normal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              !formData.travel_request_id ||
              !formData.advance_number ||
              !formData.total_cost
            }
          >
            Create Advance
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog – full advance and related travel request details */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => { setViewDialogOpen(false); setAdvanceDetails(null); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Travel Advance Details</DialogTitle>
        <DialogContent>
          {viewDetailsLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <LoadingSpinner />
            </Box>
          )}
          {!viewDetailsLoading && (advanceDetails || selectedAdvance) && (() => {
            const adv = advanceDetails || selectedAdvance;
            const status = adv.approval_status ?? adv.status;
            const employeeName = advanceDetails?.user
              ? [advanceDetails.user.first_name, advanceDetails.user.sur_name].filter(Boolean).join(" ") || "—"
              : selectedAdvance?.employee_name ?? "—";
            const approverName = advanceDetails?.approver
              ? [advanceDetails.approver.first_name, advanceDetails.approver.sur_name].filter(Boolean).join(" ") || "—"
              : null;
            const tr = advanceDetails?.travelRequest;
            return (
              <Box sx={{ pt: 0 }}>
                {/* Advance info */}
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AdvanceIcon color="primary" />
                      <Typography fontWeight="600">Advance information</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Advance number</Typography>
                        <Typography variant="body1">{adv.advance_number || `TA-${adv.id}`}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Amount</Typography>
                        <Typography variant="body1">
                          TZS {(adv.total_cost ?? adv.advance_amount)?.toLocaleString() ?? "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Departure date</Typography>
                        <Typography variant="body1">
                          {adv.departure_date ? format(new Date(adv.departure_date), "dd/MM/yyyy") : "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Return date</Typography>
                        <Typography variant="body1">
                          {adv.return_date ? format(new Date(adv.return_date), "dd/MM/yyyy") : "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Approval status</Typography>
                        <Chip
                          size="small"
                          label={advanceStatuses[status]?.label ?? status ?? "—"}
                          color={advanceStatuses[status]?.color || "default"}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Payment status</Typography>
                        <Chip
                          size="small"
                          label={paymentStatuses[adv.payment_status]?.label ?? adv.payment_status ?? "—"}
                          color={paymentStatuses[adv.payment_status]?.color || "default"}
                        />
                      </Grid>
                      {advanceDetails?.flatRate && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary">Flat rate (allowance)</Typography>
                          <Typography variant="body1">
                            {advanceDetails.flatRate.location_category ?? "—"} – {advanceDetails.flatRate.currency} {Number(advanceDetails.flatRate.amount)?.toLocaleString() ?? "—"}
                          </Typography>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Created</Typography>
                        <Typography variant="body1">
                          {adv.created_at || adv.created_date
                            ? format(new Date(adv.created_at || adv.created_date), "dd/MM/yyyy HH:mm")
                            : "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Last updated</Typography>
                        <Typography variant="body1">
                          {adv.updated_at ? format(new Date(adv.updated_at), "dd/MM/yyyy HH:mm") : "—"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                {/* Linked travel request */}
                {tr && (
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <FlightIcon color="primary" />
                        <Typography fontWeight="600">Linked travel request</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary">Request number</Typography>
                          <Typography variant="body1">{tr.request_number || `#${tr.id}`}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary">Destination</Typography>
                          <Typography variant="body1">{tr.destination ?? "—"}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="text.secondary">Purpose</Typography>
                          <Typography variant="body1">{tr.purpose ?? "—"}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary">Travel period</Typography>
                          <Typography variant="body1">
                            {tr.start_date && tr.end_date
                              ? `${format(new Date(tr.start_date), "dd/MM/yyyy")} – ${format(new Date(tr.end_date), "dd/MM/yyyy")}`
                              : "—"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary">Estimated cost</Typography>
                          <Typography variant="body1">
                            {tr.estimated_cost != null ? `TZS ${Number(tr.estimated_cost).toLocaleString()}` : "—"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary">Transport</Typography>
                          <Typography variant="body1">{tr.transport_mode ?? "—"}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary">Accommodation required</Typography>
                          <Typography variant="body1">{tr.accommodation_required != null ? (tr.accommodation_required ? "Yes" : "No") : "—"}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary">Request status</Typography>
                          <Typography variant="body1">{tr.status ?? tr.approval_status ?? "—"}</Typography>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Employee */}
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PersonIcon color="primary" />
                      <Typography fontWeight="600">Employee</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                        <Typography variant="body1">{employeeName}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                        <Typography variant="body1">{advanceDetails?.user?.email ?? "—"}</Typography>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                {/* Approval & payment */}
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <ApprovedIcon color="primary" />
                      <Typography fontWeight="600">Approval & payment</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      {approverName && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary">Approved by</Typography>
                          <Typography variant="body1">{approverName}</Typography>
                        </Grid>
                      )}
                      {adv.paid_at && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary">Paid at</Typography>
                          <Typography variant="body1">{format(new Date(adv.paid_at), "dd/MM/yyyy HH:mm")}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                {/* Rejection reason */}
                {adv.rejection_reason && (
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <RejectedIcon color="error" />
                        <Typography fontWeight="600">Rejection reason</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>{adv.rejection_reason}</Typography>
                    </AccordionDetails>
                  </Accordion>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setViewDialogOpen(false); setAdvanceDetails(null); }}>Close</Button>
          {selectedAdvance && canEditAdvance(selectedAdvance) && (
            <Button variant="outlined" startIcon={<EditIcon />} onClick={() => handleEdit(selectedAdvance)}>
              Edit Advance
            </Button>
          )}
          {selectedAdvance?.status === "rejected" && (selectedAdvance?.user_id === user?.id || hasPermission?.(PERMISSIONS.MANAGE_TRAVEL_ADVANCES)) && (
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => handleResubmit(selectedAdvance)}>
              Resubmit
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
      >
        <MenuItem
          onClick={() => {
            handleViewDetails(selectedAdvance);
            handleActionMenuClose();
          }}
        >
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          View Details
        </MenuItem>
        {canEditAdvance(selectedAdvance) && (
          <MenuItem
            onClick={() => {
              handleEdit(selectedAdvance);
              handleActionMenuClose();
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            Edit Advance
          </MenuItem>
        )}
        {selectedAdvance?.status === "rejected" && (selectedAdvance?.user_id === user?.id || hasPermission?.(PERMISSIONS.MANAGE_TRAVEL_ADVANCES)) && (
          <MenuItem
            onClick={() => {
              handleResubmit(selectedAdvance);
              handleActionMenuClose();
            }}
          >
            <ListItemIcon>
              <RefreshIcon fontSize="small" />
            </ListItemIcon>
            Resubmit Request
          </MenuItem>
        )}
      </Menu>

      {/* Edit Advance Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Travel Advance</DialogTitle>
        <DialogContent>
          {editingAdvance && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Update details before resubmitting.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Departure Date"
                    value={editFormData.departure_date}
                    onChange={(d) => setEditFormData((prev) => ({ ...prev, departure_date: d }))}
                    slotProps={{ textField: { fullWidth: true, size: "small" } }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Return Date"
                    value={editFormData.return_date}
                    onChange={(d) => setEditFormData((prev) => ({ ...prev, return_date: d }))}
                    slotProps={{ textField: { fullWidth: true, size: "small" } }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Total Cost (TZS)"
                  type="text"
                  inputMode="decimal"
                  value={editFormData.total_cost}
                  onChange={(e) => {
                    const v = String(e.target.value).replace(/,/g, "");
                    setEditFormData((prev) => ({ ...prev, total_cost: v }));
                  }}
                  size="small"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditSave}
            disabled={
              !editFormData.departure_date || !editFormData.return_date || !editFormData.total_cost || parseFloat(editFormData.total_cost) <= 0
            }
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TravelAdvancesPage;
